// @flow
import React from 'react';
import moment from 'moment';
import { LineChart, CartesianGrid, Legend, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import styled, { withTheme } from 'styled-components';

import { WEEK_START } from 'common/domain/week';
import { ISO_DATE } from 'common/domain/DateRange';
import type { HistoryDateRangeType } from 'statsradio/modules/history/domain/HistoryDateRange';
import * as HistoryDateRange from 'statsradio/modules/history/domain/HistoryDateRange';
import History from '../domain/History';
import HistoryGraphTick from './HistoryGraphTick';
import HistoryGraphTooltip from './HistoryGraphTooltip';
import HistoryGraphLegend from './HistoryGraphLegend';

const HistoryChartContainer = styled.div`
  max-width: 100%;
`;

const TICK_DATAKEY_INDEX = 2;

function formatGraphData(dateRange: HistoryDateRangeType, history: History) {
  const historyDateRange = history.getDateRange();
  const timeDateRange = HistoryDateRange.asDateRange(dateRange);

  return timeDateRange
    .mergeWith(historyDateRange)
    .trimEndToStartOfWeek()
    .getAllDates()
    .map((date) => {
      const entry = history.findStartOfWeekFor(date);

      if (!entry) {
        return {
          from: date.format(ISO_DATE),
          to: date.format(ISO_DATE),
          tick: date.format(ISO_DATE),
          tooltip: { reach: 0, listeningTime: 0 },
        };
      }

      const { from, to, reach, listeningTime, interpolated } = entry.withHourlyListeningTime();

      const payload = {
        from: from.format(ISO_DATE),
        to: to.format(ISO_DATE),
        tick: date.format(ISO_DATE),
        tooltip: { reach, listeningTime, interpolated },
      };

      if (date.isoWeekday() === WEEK_START) {
        return { ...payload, reach, listeningTime };
      }

      return payload;
    });
}

type Props = {
  data: History;
  dateRange: HistoryDateRangeType;
  onGraphClick: (selectedDate?: moment) => void;
  animate: boolean;

  theme: any;
}

class HistoryGraph extends React.Component<Props> {

  shouldComponentUpdate(nextProps) {
    return this.props.data !== nextProps.data;
  }

  onGraphClick(event: Object) {
    const eventDate = event && moment(event.activePayload[TICK_DATAKEY_INDEX].value);
    this.props.onGraphClick(eventDate);
  }

  render() {
    const { theme, data, dateRange, animate } = this.props;
    const weeklyTicks = dateRange === 'lastMonth';

    const graphData = formatGraphData(dateRange, data);

    return (
      <HistoryChartContainer>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={graphData}
            margin={{ top: 5, right: 32, bottom: 5, left: 64 }}
            onClick={event => this.onGraphClick(event)}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey={'tick'}
              height={80}
              stroke="#d8d8d8"
              strokeWidth={2}
              tickLine={false}
              tick={<HistoryGraphTick weekly={weeklyTicks} />}
              interval={0}
            />
            <YAxis yAxisId="reach" hide />
            <YAxis yAxisId="listeningTime" hide />
            <Legend content={<HistoryGraphLegend />} />
            <Line
              dataKey="reach"
              stroke={theme.colors.reach}
              strokeWidth={3}
              yAxisId="reach"
              connectNulls
              animationDuration={500}
              isAnimationActive={animate}
            />
            <Line
              dataKey="listeningTime"
              stroke={theme.colors.listeningTime}
              strokeWidth={3}
              yAxisId="listeningTime"
              connectNulls
              animationDuration={500}
              isAnimationActive={animate}
            />
            <Line
              dataKey="tick"
              stroke={'black'}
              strokeWidth={0}
              yAxisId="reach"
              dot={false}
              isAnimationActive={animate}
            />
            {animate &&
            <Tooltip content={<HistoryGraphTooltip />} />
            }
          </LineChart>
        </ResponsiveContainer>
      </HistoryChartContainer>
    );
  }
}

export default withTheme(HistoryGraph);



// WEBPACK FOOTER //
// ./src/statsradio/modules/history/components/HistoryGraph.js